import React, { Component, ErrorInfo } from 'react';
import * as Sentry from '@sentry/browser';

import { AnalyticsClient, EventAttributes } from '../utilities/analytics/analytics-web-client';
import ErrorPage from './ErrorPage';
import { FormattedMessage } from 'react-intl';
interface Props {
  children: React.ReactNode;
  analyticsClient: AnalyticsClient;
}

interface State {
  errorType?: 'network' | 'unexpected';
}

export default class ErrorBoundary extends Component<Props, State> {
  static pageId = 'unexpectedErrorPage';

  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Failed to fetch dynamically imported module:
    // Importing a module script failed.
    // error loading dynamically imported module:
    // Cannot find module
    if (/(import|find).*.module/i.test(error.message)) {
      this.setState({ errorType: 'network' });
      return;
    }

    this.setState({ errorType: 'unexpected' });

    const attributes: EventAttributes = {
      errorName: error.name,
      errorMessage: error.message,
      errorComponentStack: errorInfo.componentStack,
      errorStack: error.stack,
    };

    Sentry.withScope(scope => {
      Object.keys(errorInfo).forEach(key => {
        scope.setExtra(key, errorInfo[key]);
      });

      scope.setExtra('ErrorBoundary.action', 'loadPageFailure');
      scope.setExtra('ErrorBoundary.pageId', ErrorBoundary.pageId);

      Object.keys(attributes).forEach(key => {
        scope.setExtra(`ErrorBoundary.${key}`, attributes[key]);
      });

      Sentry.captureException(error);
    });

    this.props.analyticsClient.operationalEvent({
      page: ErrorBoundary.pageId,
      action: 'loadPageFailure',
      subject: 'sli',
      attributes,
    });
  }

  render() {
    const { errorType } = this.state;

    if (errorType === 'network') {
      return (
        <ErrorPage
          title={
            <FormattedMessage
              id="error.boundary.error.title"
              defaultMessage="Something went wrong"
            />
          }>
          <FormattedMessage
            id="error.boundary.network.error"
            defaultMessage="We were unable to load this page due to a network connectivity issue. Please check your network connection and try again."
            tagName="p"
          />
        </ErrorPage>
      );
    }

    if (errorType === 'unexpected') {
      return (
        <ErrorPage
          title={
            <FormattedMessage
              id="error.boundary.error.title"
              defaultMessage="Something went wrong"
            />
          }>
          <FormattedMessage
            id="error.boundary.unexpected.error"
            defaultMessage="An unexpected error occurred. We are looking into it. Please try again later."
            tagName="p"
          />
        </ErrorPage>
      );
    }

    return this.props.children;
  }
}
